import { UserPlain } from '../../../iam/users/types';
import { PackedImpactScoresByIVV } from '../../output/scores/types';
import { Valence } from '../../types';

// Important NOTE: Once these have been deployed, the types should be considered
// frozen. If changes need to be made, a new type should be added.

interface BasicContribution {
  comment?: string;
}

export interface CompanyMissingProductsContribution extends BasicContribution {
  type: 'COMPANY_MISSING_PRODUCTS';
  products: string[];
  newProducts: string[];
}

export interface CompanyInvalidProductsContribution extends BasicContribution {
  type: 'COMPANY_INVALID_PRODUCTS';
  products: string[];
}

export type MetadataFeedbackStatus = 'ok' | 'high' | 'low';
export interface ExistingMetadata {
  type: string; // 'revenue' or 'employees' at this point
  source: string;
  currentValue: string;
  year?: number;
  status: MetadataFeedbackStatus;
}
export interface NewMetadata {
  type: string; // 'revenue' or 'employees' at this point
  source: string;
  year: number;
  value: string;
}

export interface CompanyIncorrectMetadataContribution extends BasicContribution {
  type: 'COMPANY_INVALID_METADATA';
  existingData: ExistingMetadata[];
  newData: NewMetadata[];
}

export interface WeirdImpact {
  impactCode: string;
  valence: Valence;
  status: 'missing' | 'too-small' | 'too-big' | 'should-not-exist';
}

export interface CompanyWeirdImpactContribution extends BasicContribution {
  type: 'COMPANY_WEIRD_IMPACT';
  revenue?: number;
  impacts: WeirdImpact[];
  currentScores: PackedImpactScoresByIVV;
}

export interface ProductWeirdImpactContribution extends BasicContribution {
  type: 'PRODUCT_WEIRD_IMPACT';
  impacts: WeirdImpact[];
  currentScores: PackedImpactScoresByIVV;
}

export interface ProductKnowledgeContribution extends BasicContribution {
  type: 'PRODUCT_KNOWLEDGE';
  upstreamProducts: string[];
  newUpstreamProducts: string[];
  downstreamProducts: string[];
  newDownstreamProducts: string[];
}

export interface ProductInvalidContribution extends BasicContribution {
  type: 'PRODUCT_INVALID';
}

export interface NewProductContribution extends BasicContribution {
  type: 'NEW_PRODUCT';
  newProduct: string;
  closestProduct: string;
  comment: string;
}

export interface NewCompanyContribution extends BasicContribution {
  type: 'NEW_COMPANY';
  newCompany: {
    wikipediaURL?: string;
    name?: string;
    website?: string;
  };
  products: string[];
  newProducts: string[];
}

export interface OpenCommentContribution extends BasicContribution {
  type: 'OPEN_COMMENT';
  contacts?: string;
  location: string;
}

export type Contribution =
  | CompanyMissingProductsContribution
  | CompanyInvalidProductsContribution
  | CompanyIncorrectMetadataContribution
  | CompanyWeirdImpactContribution
  | ProductKnowledgeContribution
  | ProductWeirdImpactContribution
  | OpenCommentContribution
  | ProductInvalidContribution
  | NewProductContribution
  | NewCompanyContribution;

export type ContributionType = Contribution['type'];

export interface ContributionPublic {
  id: number;
  type: ContributionType;
  updatedAt: Date;
  userId: number;
  targetId?: string;
  targetName?: string;
  /**
   * E.g. suggested products for company
   */
  content?: Array<{ id: string; name?: string }>;
  /**
   * E.g. suggested new products or companies
   */
  newContent?: string[];
}

export interface RequestCreateContribution {
  type: ContributionType;
  target?: string;
  data: Contribution;
}

export interface ContributionWithUser {
  user: UserPlain;
  contribution: ContributionPublic;
}

export type ResponseCreateContribution = ContributionWithUser;

export interface ResponseCreateAnonymousContribution {}

export type ResponseContributions = ContributionWithUser[];

export type ResponseDeleteContribution = UserPlain;

export type ResponseAllContributions = Array<{
  id: number;
  modelVersion: string;
  updatedAt: string;
  username?: string;
  comment?: string;
  type: ContributionType;
  data: Contribution;
  target?: string;
  targetName?: string;
}>;

export const ALL_CONTRIBUTION_TYPES: ContributionType[] = [
  'COMPANY_MISSING_PRODUCTS',
  'COMPANY_INVALID_PRODUCTS',
  'COMPANY_INVALID_METADATA',
  'COMPANY_WEIRD_IMPACT',
  'PRODUCT_KNOWLEDGE',
  'PRODUCT_WEIRD_IMPACT',
  'PRODUCT_INVALID',
  'NEW_PRODUCT',
  'NEW_COMPANY',
  'OPEN_COMMENT',
];

export const COMPANY_CONTRIBUTION_TYPES: ContributionType[] = [
  'COMPANY_INVALID_METADATA',
  'COMPANY_INVALID_PRODUCTS',
  'COMPANY_MISSING_PRODUCTS',
  'COMPANY_WEIRD_IMPACT',
];

export const PRODUCT_CONTRIBUTION_TYPES: ContributionType[] = [
  'PRODUCT_KNOWLEDGE',
  'PRODUCT_WEIRD_IMPACT',
  'PRODUCT_INVALID',
];

export function isCompanyContribution(type: ContributionType) {
  return COMPANY_CONTRIBUTION_TYPES.indexOf(type) > -1;
}

export function isProductContribution(type: ContributionType) {
  return PRODUCT_CONTRIBUTION_TYPES.indexOf(type) > -1;
}
